import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
  TrackByFunction,
  Inject,
  inject,
  LOCALE_ID
} from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { CONSTANTS_TOKEN } from '../../../config';
import { PortalService, UtilsService } from '../../../services';
import { DocumentViewerComponent } from '../document-viewer/document-viewer.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { GrantAccessDialogComponent } from '../grant-access-dialog/grant-access-dialog.component';
import { AreaOfInterestDialogComponent } from '../../area-of-interest-dialog/area-of-interest-dialog.component';
import { FolderContentsComponent } from '../folder-contents/folder-contents.component';
import { DateRangePickerComponent } from '../../date-range-picker/date-range-picker.component';
import { TableComponent } from '../../table/table.component';
import { SnackBar, TableActionType } from '../../../enums';
import { DocumentItem, MIME_TYPE_ICONS, EXT_ICONS } from '../document.model';
import { FileIconComponent, FileIconType } from '../file-icon/file-icon.component';
import { ViewModeToggleComponent, ViewMode } from '../view-mode-toggle/view-mode-toggle.component';

@Component({
  selector: 'educare-document-browser',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    TranslateModule,
    DocumentViewerComponent,
    MatCheckboxModule,
    FolderContentsComponent,
    DateRangePickerComponent,
    TableComponent,
    FileIconComponent,
    ViewModeToggleComponent
  ],
  templateUrl: './document-browser.component.html',
  styleUrls: ['./document-browser.component.scss']
})
export class DocumentBrowserComponent implements OnInit {
  @Input() pageSize = 20;
  @Input() showPagination = true;
  @Input() showActions = false;
  /**
   * When true, hides the in-browser keyword search row and tightens top spacing on the filter toolbar (User Portal
   * Knowledge Hub only). Default false keeps the bar and default margins for Admin Portal and routed usages.
   */
  @Input() hideHeaderSearch = false;
  get enableSelection(): boolean {
    return this.constants?.app?.portalType === 'admin';
  }
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  @Output() itemClick = new EventEmitter<DocumentItem>();
  @Output() selectionChange = new EventEmitter<DocumentItem[]>();
  @Input() showViewer = false;
  @Input() selectedDocument: DocumentItem | null = null;

  selectedItems: DocumentItem[] = [];
  selectAll = false;

  @ViewChild(FolderContentsComponent) folderContentsComponent?: FolderContentsComponent;

  items: DocumentItem[] = [];
  loading = false;
  viewMode: ViewMode = 'list';
  currentPage = 0;
  totalItems = 0;
  searchValue = '';
  showFolderContents = false;
  selectedFolder: DocumentItem | null = null;
  folderNavigationStack: DocumentItem[] = [];
  filterForm!: FormGroup;
  filters: any = {
    filter: {},
    pagination: {
      limit: 10,
      page: 1
    },
    sort: undefined
  };

  fileTypes = [
    { id: 'pdf', name: this.utils.translation('fileType.pdf') },
    { id: 'docx', name: this.utils.translation('fileType.docx') },
    { id: 'xlsx', name: this.utils.translation('fileType.xlsx') },
    { id: 'doc', name: this.utils.translation('fileType.doc') },
    { id: 'xls', name: this.utils.translation('fileType.xls') },
    { id: 'csv', name: this.utils.translation('fileType.csv') }
  ];

  sortOptions = [
    { id: 'name', name: this.utils.translation('sortingFilter.name') },
    { id: 'modified_date', name: this.utils.translation('sortingFilter.dateModified') },
    { id: 'file_type', name: this.utils.translation('sortingFilter.fileType') }
  ];

  sortOrder = [
    { id: 'asc', name: this.utils.translation('sortingFilter.asc') },
    { id: 'desc', name: this.utils.translation('sortingFilter.desc') }
  ];

  private readonly dialog = inject(MatDialog);
  TableActionType = TableActionType;
  private filterTimeout: any;

  private static readonly AOI_DIALOG_CONFIG = {
    width: '420px',
    maxWidth: '95vw',
    panelClass: 'area-of-interest-dialog-panel'
  } as const;

  get columns() {
    const baseColumns: any[] = [
      {
        name: 'name',
        title: this.utils.translation('knowldegeHubAdminPortal.columnsName.title'),
        onClick: (_$event: any, element: DocumentItem) => {
          this.onItemClick(element);
        }
      },
      {
        name: 'updatedDate',
        title: this.utils.translation('knowldegeHubAdminPortal.columnsName.updatedDate')
      },
      {
        name: 'updatedBy',
        title: this.utils.translation('knowldegeHubAdminPortal.columnsName.updatedBy')
      }
    ];

    if (this.showActions) {
      (baseColumns as any).push({
        name: 'actions',
        title: this.utils.translation('knowldegeHubAdminPortal.columnsName.actions'),
        type: TableActionType.IconButton,
        stickyEnd: true,
        actions: [
          {
            id: 'areaOfInterest',
            iconClass: 'das-bookmark_heart fs-4',
            tooltip: this.utils.translation('common.areaOfInterest.label'),
            disabled: () => this.selectedItems.length > 0
          },
          {
            id: 'grantAccess',
            iconClass: 'das-article_person fs-4',
            tooltip: this.utils.translation('common.grantAccess'),
            disabled: () => this.selectedItems.length > 0
          },
          {
            id: 'revokeAccess',
            iconClass: 'das-undo fs-4',
            tooltip: this.utils.translation('common.revokeAccess'),
            disabled: () => this.selectedItems.length > 0
          }
        ],
        onClick: (_$event: any, element: DocumentItem, actionId: string) => {
          if (actionId === 'areaOfInterest') {
            this.openAreaOfInterestDialog([element.id]);
          } else {
            this.openGrantAccessDialog(actionId === 'revokeAccess', element);
          }
        }
      });
    }

    return baseColumns;
  }

  get tableDataSource() {
    return {
      items: this.items,
      count: this.totalItems,
      page: this.currentPage + 1,
      limit: this.pageSize
    };
  }

  get tableSelectionConfig() {
    if (!this.enableSelection) {
      return undefined;
    }
    return {
      enabled: true,
      selectedItems: this.selectedItems.map((item) => item.id),
      selectAll: this.selectAll,
      selectionKey: 'id',
      totalCount: this.totalItems,
      selectedCount: this.selectedItems.length
    };
  }

  constructor(
    @Inject(CONSTANTS_TOKEN) private constants: any,
    private portalService: PortalService,
    private utils: UtilsService,
    private fb: FormBuilder,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.filterForm = this.fb.group({
      file_type: [''],
      uploaded_by: [''],
      status: [''],
      start: [null],
      end: [null],
      sort_by: [''],
      sort_order: ['']
    });
  }

  ngOnInit() {
    this.showActions = this.constants.app.portalType === 'admin';
    const navState = this.readNavigationState();
    if (typeof navState.keyword === 'string' && navState.keyword) {
      this.searchValue = navState.keyword;
    }
    this.loadDocuments();
    this.scheduleOpenFromNavigationTarget(navState.knowledgeHubTarget);
  }

  private readNavigationState(): { keyword?: unknown; knowledgeHubTarget?: unknown } {
    const s = typeof history !== 'undefined' ? history.state : null;
    return s && typeof s === 'object' ? (s as Record<string, unknown>) : {};
  }

  /** Opens folder contents or document viewer when unified search (or similar) passes a row in router state. */
  private scheduleOpenFromNavigationTarget(target: unknown): void {
    if (!target || typeof target !== 'object' || Array.isArray(target)) {
      return;
    }
    const item = this.transformDocument(target as Record<string, unknown>);
    if (!item?.id) {
      return;
    }
    queueMicrotask(() => this.onItemClick(item));
  }

  loadDocuments() {
    this.loading = true;
    const params = new URLSearchParams({
      page: (this.currentPage + 1).toString(),
      limit: this.pageSize.toString()
    });

    if (this.searchValue.trim()) {
      params.append('search', this.searchValue.trim());
    }

    if (this.filters.filter) {
      Object.keys(this.filters.filter).forEach((key) => {
        const value = this.filters.filter[key];
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });
    }

    const queryString = params.toString();
    const url = `${this.utils.getEncryptedUrls(this.constants.api.privateEndpoints.searchDocument)}?${queryString}`;

    this.portalService.get(url).subscribe({
      next: (response: any) => {
        const documents = response?.data?.documents || [];
        this.totalItems = response?.data?.count || response?.data?.total;
        this.items = documents.map((doc: any) => this.transformDocument(doc));
        this.loading = false;
      },
      error: (error: any) => {
        this.utils.openSnackBar(error.error.message, 'Close', SnackBar.Error);
        this.loading = false;
      }
    });
  }

  private transformDocument(doc: any): DocumentItem {
    const updatedDate = doc.last_modified_date_time || doc.lastModifiedDateTime;
    return {
      id: doc.id || doc.document_id,
      name: doc.name || doc.title || doc.document_name,
      type: doc.type || doc.document_type,
      updatedDate: updatedDate ? formatDate(updatedDate, 'dd-MM-yyyy', this.locale) : '',
      updatedBy: doc.last_modified_by || doc.updatedBy || doc.lastModifiedBy,
      title: doc.title,
      fileCount: doc.fileCount ? doc.fileCount : doc.child_count
    };
  }

  getFileIcon(item: DocumentItem): FileIconType {
    if (item.type === 'folder') {
      return 'folder';
    }
    if (item.mimeType) {
      const icon = MIME_TYPE_ICONS[item.mimeType.toLowerCase()];
      if (icon) {
        return icon;
      }
    }
    if (item.fileExtension) {
      const icon = EXT_ICONS[item.fileExtension.toLowerCase()];
      if (icon) {
        return icon;
      }
    }
    return 'doc';
  }

  onViewModeToggle(mode: ViewMode) {
    this.viewMode = mode;
    this.viewModeChange.emit(mode);
    this.currentPage = 0;
    this.loadDocuments();
  }

  onItemClick(item: DocumentItem) {
    if (item.type === 'folder') {
      this.folderNavigationStack = [item];
      this.selectedFolder = item;
      this.showFolderContents = true;
    } else {
      this.selectedDocument = item;
      this.showViewer = true;
    }
    this.itemClick.emit(item);
  }

  onViewerBack() {
    this.showViewer = false;
    this.selectedDocument = null;
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.loadDocuments();
  }

  trackByFn: TrackByFunction<DocumentItem> = (_index, item) => item.id;

  searchOnEnter(event: Event) {
    event.preventDefault();
    this.searchDocuments();
  }

  searchDocuments() {
    this.currentPage = 0;
    this.loadDocuments();
  }

  clearSearch() {
    this.searchValue = '';
    this.searchDocuments();
  }

  onTableSelectionChange(selectedIds: string[]): void {
    this.selectedItems = this.items.filter((item) => selectedIds.includes(item.id));
    this.selectAll = selectedIds.length === this.items.length && this.items.length > 0;
    this.selectionChange.emit(this.selectedItems);
  }

  onTableSelectAllAcrossPages(selectAll: boolean): void {
    this.selectAll = selectAll;
    if (selectAll) {
      this.selectedItems = [...this.items];
    } else {
      this.selectedItems = [];
    }
    this.selectionChange.emit(this.selectedItems);
  }

  toggleItemSelection(item: DocumentItem, checked: boolean): void {
    if (checked) {
      if (!this.selectedItems.some((selected) => selected.id === item.id)) {
        this.selectedItems.push(item);
      }
    } else {
      this.selectedItems = this.selectedItems.filter((selected) => selected.id !== item.id);
    }
    this.selectionChange.emit(this.selectedItems);
  }

  isItemSelected(item: DocumentItem): boolean {
    return this.selectedItems.some((selected) => selected.id === item.id);
  }

  openGrantAccessDialog(isRevokeMode: boolean, document?: DocumentItem): void {
    const selectedDocuments = document ? [document] : this.selectedItems;
    const shouldClearSelection = !document;

    const dialogRef = this.dialog.open(GrantAccessDialogComponent, {
      data: {
        isRevokeMode,
        selectedDocuments,
        source: 'document-browser'
      },
      width: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(() => {
      if (shouldClearSelection) {
        this.clearSelection();
      }
    });
  }

  openAreaOfInterestDialog(documentIds?: string[]): void {
    const ids = documentIds ?? this.selectedItems.map((d) => d.id);
    const isBulk = !documentIds;

    this.dialog
      .open(AreaOfInterestDialogComponent, {
        ...DocumentBrowserComponent.AOI_DIALOG_CONFIG,
        data: { initialSelected: [], documentIds: ids }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.assignSuccess) {
          isBulk ? this.clearSelection() : this.loadDocuments();
        }
      });
  }

  private clearSelection(): void {
    this.selectedItems = [];
    this.selectAll = false;
    this.selectionChange.emit(this.selectedItems);
  }

  onFolderBack() {
    this.folderNavigationStack.pop();
    if (this.folderNavigationStack.length > 0) {
      this.selectedFolder = this.folderNavigationStack[this.folderNavigationStack.length - 1];
    } else {
      this.showFolderContents = false;
      this.selectedFolder = null;
      this.clearSelection();
    }
  }

  onFolderItemClick(item: any) {
    if (item.type === 'folder') {
      this.folderNavigationStack.push(item);
      this.selectedFolder = item;
      if (this.folderContentsComponent) {
        this.folderContentsComponent.clearSelection();
      }
    } else {
      this.selectedDocument = item;
      this.showViewer = true;
      this.showFolderContents = false;
    }
  }

  onFolderSelectionChange(items: any[]) {
    this.selectedItems = items as DocumentItem[];
    this.selectionChange.emit(this.selectedItems);
  }

  onFilterChange(event: Event, filterType: string) {
    const filter = (event.target as HTMLInputElement)?.value;
    if (filterType === 'sort_by') {
      if (filter === 'asc' || filter === 'desc') {
        this.filters.filter['sort_order'] = filter;
      } else {
        this.filters.filter['sort_by'] = filter;
      }
    } else {
      this.filters.filter[filterType] = filter;
    }

    this.filters.pagination.page = 1;

    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }

    this.filterTimeout = setTimeout(() => {
      this.loadDocuments();
    }, 1000);
  }

  applyDateFilter(dateRange: { start: string; end: string }): void {
    this.filters.filter.start_date = dateRange.start;
    this.filters.filter.end_date = dateRange.end;
    this.filters.pagination.page = 1;
    this.loadDocuments();
  }
}
